/**
 * NietGeluktDialog — bevestigingsdialoog voor de office-actie "ophaal/bezorging
 * niet gelukt vandaag → klant herplant zelf".
 *
 * Werkt voor één stop (per-stop knop) én meerdere stops (bulk-selectie). Toont
 * de betrokken klant(en)/adres(sen), een optioneel notitieveld en een "informeer
 * de klant per e-mail"-toggle (default aan). Bij bevestigen gaat de werkorder
 * terug naar '...plannen' en krijgt de klant (optioneel) een self-service
 * herplan-mail.
 */

import { useState, useCallback, useEffect } from 'react';
import type { RouteStop } from '../../lib/salesforce/types';

interface NietGeluktDialogProps {
  stops: RouteStop[];
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (opts: { notitie?: string; informeerKlant: boolean }) => void;
  isSubmitting: boolean;
}

export function NietGeluktDialog({
  stops,
  isOpen,
  onClose,
  onConfirm,
  isSubmitting,
}: NietGeluktDialogProps) {
  const [notitie, setNotitie] = useState('');
  const [informeerKlant, setInformeerKlant] = useState(true);

  // Reset de velden telkens als de dialog opent met een nieuwe selectie
  useEffect(() => {
    if (isOpen) {
      setNotitie('');
      setInformeerKlant(true);
    }
  }, [isOpen]);

  const handleConfirm = useCallback(() => {
    onConfirm({ notitie: notitie.trim() || undefined, informeerKlant });
  }, [notitie, informeerKlant, onConfirm]);

  if (!isOpen) return null;

  const count = stops.length;
  const meervoud = count !== 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Dialog */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          {meervoud ? `${count} stops niet gelukt` : 'Stop niet gelukt'}
        </h3>

        <p className="text-sm text-gray-600 mb-4">
          {meervoud
            ? 'De werkorders gaan terug naar de plannings-fase en de klanten kunnen zelf een nieuw moment kiezen via de link in de mail.'
            : 'De werkorder gaat terug naar de plannings-fase en de klant kan zelf een nieuw moment kiezen via de link in de mail.'}
        </p>

        {/* Betrokken klant(en) */}
        <div className="mb-4 max-h-40 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
          {stops.map((stop) => (
            <div key={stop.id} className="flex items-center justify-between gap-2 px-3 py-2">
              <div className="min-w-0">
                <div className="font-medium text-gray-900 truncate">
                  {stop.accountName || stop.contactName || 'Onbekende klant'}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {stop.street}, {stop.postalCode} {stop.city}
                </div>
              </div>
              <span
                className={`shrink-0 px-1.5 py-0.5 rounded text-xs font-medium ${
                  stop.stopType === 'Ophalen'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-emerald-100 text-emerald-700'
                }`}
              >
                {stop.stopType}
              </span>
            </div>
          ))}
        </div>

        {/* Notitie */}
        <div className="mb-4">
          <label htmlFor="niet-gelukt-notitie" className="block text-sm font-medium text-gray-700 mb-1">
            Notitie <span className="font-normal text-gray-400">(optioneel)</span>
          </label>
          <textarea
            id="niet-gelukt-notitie"
            value={notitie}
            onChange={(e) => setNotitie(e.target.value)}
            rows={2}
            placeholder="Bijv. route niet gehaald / te krap ingepland"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
          />
          <p className="mt-1 text-xs text-gray-400">
            Komt op de rit en in de planningsnotitie van de werkorder.
          </p>
        </div>

        {/* Informeer klant toggle */}
        <label className="flex items-start gap-3 p-3 mb-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
          <input
            type="checkbox"
            checked={informeerKlant}
            onChange={(e) => setInformeerKlant(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <div>
            <div className="font-medium text-gray-900 text-sm">Informeer de klant per e-mail</div>
            <div className="text-xs text-gray-500">
              Stuurt een nette excuus-mail met een link om zelf gratis een nieuw moment te kiezen.
            </div>
          </div>
        </label>

        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting || count === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Bezig…
              </>
            ) : (
              'Markeer als niet gelukt'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
